import { handleActions } from 'redux-actions';
import { replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import range from 'lodash/range';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import qs from 'qs';

import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import csvExport from '../shared/csvExport';
import checkPermissions from '../shared/check-permissions';

import createFilters from '../shared/filters';

const filters = createFilters({
	namespace: 'campaignsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'has_specific_payouts',
			title: 'Has specific campaign payouts',
			placeholder: 'Has specific campaign payouts',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: [ true ].map(src => ({ value: src, label: src.toString() }))
			}
		},
		{
			name: 'currencies',
			title: 'Currencies',
			placeholder: 'Add currencies',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.available-currencies'
			}
		},
		{
			name: 'language',
			title: 'Language',
			placeholder: 'Add language',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.landing-languages'
			}
		},
		{
			name: 'country',
			title: 'Country',
			placeholder: 'Country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?is_country_codes=true&limit=20`
			}
		},
		{
			name: 'campaign_types',
			title: 'Campaign types',
			placeholder: 'Add campaign types',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.campaign-types'
			}
		},
		{
			name: 'referer_types',
			title: 'Referer types',
			placeholder: 'Add referer types',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.referer-types'
			}
		},
		{
			name: 'campaign_domains',
			title: 'Domains',
			placeholder: 'Domains',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.CAMPAIGN_DOMAINS
			}
		}
	]
});

// actions types
const FETCH_DATA_SUCCESS = 'campaigns-list/fetch-data-success';
const FETCH_DATA_ERROR = 'campaigns-list/fetch-data-error';

const SORT_BY_COLUMN = 'campaigns-list/table-sort-by-column';
const GOTO_PAGE_SUCCESS = 'campaigns-list/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'campaigns-list/toggle-progress-bar';

const CLEAR = 'campaigns-list/clear';

const UPDATE_REFERER_TYPE_SUCCESS = 'campaigns-list/update-referer-type-success';
const UPDATE_REFERER_TYPE_ERROR = 'campaigns-list/update-referer-type-error';

const TOGGLE_AUTOARCHIVING = 'campaigns-list/toggle-autoarchiving';

const FETCH_CAMPAIGNS_FOR_CSV = 'campaigns-list/FETCH_CAMPAIGNS_FOR_CSV';

// permissions
export const allowedPermissions = {
	view: 'campaigns_list',
	campaigns_edit: 'campaigns_edit'
};

// initial state
const initialState = {
	data: null,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'ID',
				direction: 'desc'
			},
			created_at: {
				index: 'created_at',
				title: 'Created at',
				direction: 'desc'
			},
			currency: {
				index: 'currency',
				title: 'Curr.',
				direction: 'desc'
			},
			title: {
				index: 'title',
				title: 'Title'
			},
			affiliate: {
				index: 'affiliate',
				title: 'Affiliate',
				direction: 'desc'
			},
			offer: {
				index: 'offer',
				title: 'Offer',
				direction: 'desc'
			},
			domains: {
				index: 'domains',
				title: 'Domains'
			},
			is_archived: {
				index: 'is_archived',
				title: 'Is Archived'
			},
			referer_type: {
				index: 'referer_type',
				title: 'Referer type',
				direction: 'desc'
			},
			actions: {
				index: 'actions',
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'created_at',
			title: 'Created at',
			direction: 'desc'
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	filters: filters.initialState,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { pagination: { perPage } } = getState().campaignsList;
			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'campaignsList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`campaigns/?${qs.stringify(savedFilters)}`));
			}
			return request(0, perPage, getState, dispatch, externalQuery && externalQuery.query).then(action => {
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		updateRefererType: (val, id) => dispatch => {
			const value = val && val[0] && val[0].value || null;

			return http({
				url: `${endpoints.CAMPAIGNS.BASE}/${id}/referer-type`,
				method: 'put',
				actions: [UPDATE_REFERER_TYPE_SUCCESS, UPDATE_REFERER_TYPE_ERROR],
				data: { referer_type: value },
				addToResponse: {
					id,
					type: value
				}
			})(dispatch);
		},

		search: () => (dispatch, getState) => {
			const { pagination: { perPage } } = getState().campaignsList;

			request(0, perPage, getState, dispatch);
		},

		apply: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			const { pagination: { perPage } } = getState().campaignsList;
			return request(0, perPage, getState, dispatch);
		},

		exportCsv: () => (dispatch, getState) => {

			const headers = [
				'id', 'created_at', 'currency', 'title', 'affiliate', 'offer', 'domains',
				'is_archived', 'referer_type', 'manager'
			];

			const query = getFetchQuery( 0,  100000, getState, {});

			return http({
				url: `${endpoints.CAMPAIGNS.BASE}?${query}`,
				method: 'get',
				actions: [ FETCH_CAMPAIGNS_FOR_CSV ]
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_CAMPAIGNS_FOR_CSV && _get(action, 'payload.data.rows.length')) {
						const rows = _get(action, 'payload.data.rows').map(row => ({
							...row, domains: `${row.landing_domain}, ${row.transit_domain || ''}`
						}));

						const columns = rows.map(entity => headers.map(index => entity[index] || '-'));

						const rowsForExport = [].concat([headers], columns);
						csvExport('Campaigns', rowsForExport);
					}
				});
		},
		clear: () => (dispatch, getState) => {
			dispatch(replace('/campaigns'));
			dispatch({
				type: CLEAR
			});
		},

		sortByColumn: columnIndex => (dispatch, getState) => {
			dispatch({ type: SORT_BY_COLUMN, payload: columnIndex });
			dispatch(actions.fetchData({}));
		},

		gotoPage: page => (dispatch, getState) => {
			const { perPage } = getState().campaignsList.pagination;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			request(offset, perPage, getState, dispatch).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		toggleAutoArchiving: (campaign_ids, is_ignored_autoremove = true) => (dispatch, getState) => {
			return http({
				url: `${endpoints.CAMPAIGNS.BASE}/toggle-autoarchiving`,
				method: 'put',
				data: { campaign_ids: campaign_ids, is_ignored_autoremove },
				actions: [ TOGGLE_AUTOARCHIVING ]
			})(dispatch)
				.then(action => {
					if (action.type === TOGGLE_AUTOARCHIVING) {
						dispatch(actions.apply());
					}
				});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn['index'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;
			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[CLEAR]: state => ({ ...initialState }),
		[UPDATE_REFERER_TYPE_SUCCESS]: (state, action) => {
			return {
				...state,
				data: state.data.map(item => {
					if (item.id === action.meta.id) {
						return { ...item, referer_type: action.meta.type };
					}
					return item;
				})
			};
		},
		[UPDATE_REFERER_TYPE_ERROR]: state => ({ ...state, error: true })
	},
	filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.campaignsList.data,
	getTable: state => {
		const { table, data } = state.campaignsList;
		const rows = data || [];
		return {
			headers: values(table.columns),
			rows: rows.slice(0, table.rowsNumber),
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getSortByColumn: state => _get(state, 'campaignsList.table.sortByColumn', {}),
	getPagination: state => {
		const { perPage, total, currentPage } = state.campaignsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage
		};
	},
	getFilters: state => filters.selectors.getFilters(state => state.campaignsList.filters, state => state)(state),
	getRefererTypeOptions: state => state.root.lists['referer-types'].map(i => ({ label: i.text, value: i.id })),
	getIntl: state => intl(state),
	getProgressBar: state => state.campaignsList.showProgressBar,
	getListedCampaignIds: state => {
		const table = selectors.getTable(state);

		return table.rows.map(row => row && row.id);
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.campaignsList.error
};

// functions

const request = (offset = 0, limit = 50, getState, dispatch, externalQuery) => {
	const query = getFetchQuery(offset, limit, getState, externalQuery);

	return http({
		url: `${endpoints.CAMPAIGNS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	})(dispatch);
};

function getFetchQuery(offset = 0, limit = 50, getState, externalQuery) {
	const { filters: { selectedFilters } } = getState().campaignsList;
	const searchForm = getFormValues('campaignsSearchForm')(getState());
	const showActiveCampaignsForm = getFormValues('showActiveCampaignsForm')(getState());
	const search = searchForm && searchForm.search || '';
	const filters = {};

	if (selectedFilters) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				filters[filter.name] = values;
			}
		});
	}

	return qs.stringify({
		offset,
		limit,
		filters,
		is_showed_archived:  showActiveCampaignsForm && showActiveCampaignsForm.is_showed_archived || '',
		sort: selectors.getSortByColumn(getState()),
		search,
		...externalQuery
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/campaigns/index.js